/** Parse raw stream event payloads into typed widget data. */

import type { QuickAction } from '../types';
import type { CarouselData, ChartData, DisplayFormData, DisplayFormField, DisplayFormFieldOption, ToolCallData } from './types';

/**
 * Parse a display_carousel event payload into CarouselData. The button target
 * is read as `component_id` or a stored payload's `form_id`.
 */
export function parseCarouselEvent(parsed: Record<string, unknown>): CarouselData {
  return {
    items: ((parsed.items as Record<string, unknown>[]) || []).map(
      (item: Record<string, unknown>) => ({
        id: item.id as string,
        title: item.title as string,
        description: item.description as string | undefined,
        imageUrl: item.image_url as string | undefined,
        linkUrl: item.link_url as string | undefined,
        primaryValue: item.primary_value as string | undefined,
        secondaryValue: item.secondary_value as string | undefined,
        badge: item.badge as string | undefined,
        metadata: item.metadata as Record<string, unknown> | undefined,
      }),
    ),
    title: parsed.title as string | undefined,
    totalCount: parsed.total_count as number | undefined,
    button: parsed.button
      ? {
          title: (parsed.button as Record<string, unknown>).title as string,
          actionType: (parsed.button as Record<string, unknown>).action_type as string,
          formId: ((parsed.button as Record<string, unknown>).component_id ??
            (parsed.button as Record<string, unknown>).form_id) as string,
        }
      : undefined,
  };
}

/** Parse a display_form event payload into DisplayFormData. */
export function parseDisplayFormEvent(parsed: Record<string, unknown>): DisplayFormData {
  return {
    form_id: parsed.form_id as string,
    render_event_id: (parsed.render_event_id as string | undefined) ?? undefined,
    title: parsed.title as string,
    description: parsed.description as string | null,
    fields: ((parsed.fields as Record<string, unknown>[]) || []).map(
      (field: Record<string, unknown>): DisplayFormField => ({
        id: field.id as string,
        label: field.label as string,
        name: field.name as string,
        field_type: field.field_type as string,
        placeholder: field.placeholder as string | null,
        help_text: field.help_text as string | null,
        is_required: field.is_required as boolean,
        options: parseFieldOptions(field.options),
        validation: field.validation as Record<string, unknown> | null,
        default_value: field.default_value as DisplayFormField['default_value'],
        order: field.order as number,
      }),
    ),
    start_open: (parsed.start_open as boolean) ?? false,
    allow_multiple_submissions: (parsed.allow_multiple_submissions as boolean) ?? false,
  };
}

/** A field's options with a Brain form's `answer` normalized to `value`. */
function parseFieldOptions(options: unknown): DisplayFormField['options'] {
  if (!Array.isArray(options)) return options as DisplayFormField['options'];
  return options.map(
    (option: Record<string, unknown>): DisplayFormFieldOption => ({
      value: ('value' in option ? option.value : option.answer) as string,
      label: option.label as string,
    }),
  );
}

/** Stream event callbacks (used by both WS and SSE consumers) */
export interface StreamCallbacks {
  onToken: (content: string) => void;
  onTextReplace?: (content: string) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
  onDisplayCarousel?: (carousel: CarouselData) => void;
  onDisplayForm?: (form: DisplayFormData) => void;
  onDisplayChart?: (chart: ChartData) => void;
  onTurnFailed?: () => void;
  onToolCall?: (toolCall: ToolCallData) => void;
  onCarouselBuilding?: () => void;
  onQuickActions?: (actions: QuickAction[]) => void;
  onQuickActionsLoading?: () => void;
}

/** Parse a `quick_actions` payload into the chips the widget draws. */
export function parseQuickActions(data: Record<string, unknown>): QuickAction[] {
  const actions = Array.isArray(data.actions)
    ? (data.actions as Array<{
        label: string;
        search_query?: string | null;
        form_id?: string | null;
        bg_color?: string | null;
        text_color?: string | null;
      }>)
    : [];
  return actions.map((a) => ({
    label: a.label,
    search_query: a.search_query ?? null,
    form_id: a.form_id ?? null,
    bg_color: a.bg_color ?? null,
    text_color: a.text_color ?? null,
  }));
}

/** Process a single stream event (from WS stream_chunk) and dispatch to callbacks. */
export function processStreamEvent(
  eventType: string,
  data: Record<string, unknown>,
  callbacks: StreamCallbacks,
): void {
  if (eventType === 'display_carousel' && callbacks.onDisplayCarousel) {
    callbacks.onDisplayCarousel(parseCarouselEvent(data));
  } else if (eventType === 'display_form' && callbacks.onDisplayForm) {
    callbacks.onDisplayForm(parseDisplayFormEvent(data));
  } else if (eventType === 'display_chart' && callbacks.onDisplayChart) {
    callbacks.onDisplayChart(data as unknown as ChartData);
  } else if (eventType === 'carousel_building' && callbacks.onCarouselBuilding) {
    callbacks.onCarouselBuilding();
  } else if (eventType === 'quick_actions_loading' && callbacks.onQuickActionsLoading) {
    callbacks.onQuickActionsLoading();
  } else if (eventType === 'quick_actions' && callbacks.onQuickActions) {
    callbacks.onQuickActions(parseQuickActions(data));
  } else if (eventType === 'tool_call' && callbacks.onToolCall) {
    callbacks.onToolCall({
      toolName: data.tool_name as string,
      displayName: data.display_name as string,
      status: data.status as string,
      executionMessage: (data.execution_message as string) ?? null,
    });
  } else if (eventType === 'token') {
    callbacks.onToken((data.content as string) || '');
  } else if (eventType === 'text_replace' && callbacks.onTextReplace) {
    callbacks.onTextReplace((data.content as string) || '');
  } else if (eventType === 'complete') {
    callbacks.onComplete();
  } else if (eventType === 'error') {
    callbacks.onError(new Error((data.message as string) || 'An error occurred'));
  }
}
